import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BackendService } from 'src/app/services/backend.service';
import { CaseMasterRoleModel, OrgCaseRoleModel } from 'src/app/models/apiRequestModels';

// One editable row in the Manage Roles grid. id === 0 means a new (unsaved) role.
interface OrgRoleRow {
  id: number;
  roleCode: string;
  displayName: string;
  description: string;
  displayOrder: number;
  isActive: boolean;
}

/**
 * Manage an organization's role catalog. Two modes, chosen from whether the org already has roles:
 *  - 'assign': first-time setup - pick roles from the global master list to copy into the org catalog.
 *  - 'manage': the org already has roles - add / edit / reorder / activate-deactivate / delete, then Save.
 * The global master list is never modified. Closes with `true` when the catalog changed so the opener reloads.
 */
@Component({
  selector: 'app-manage-org-roles-dialog',
  templateUrl: './manage-org-roles-dialog.component.html',
  styleUrls: ['./manage-org-roles-dialog.component.css']
})
export class ManageOrgRolesDialogComponent implements OnInit {

  orgExtId = '';
  loading = true;
  saving = false;
  errorMessage = '';
  // True when the initial catalog/master load failed, so the dialog shows a clean error (not the
  // empty Assign screen). Distinct from errorMessage, which also carries in-mode validation/save errors.
  loadFailed = false;
  mode: 'assign' | 'manage' = 'assign';

  // assign mode
  masterRoles: CaseMasterRoleModel[] = [];
  selectedMasterRoleIds = new Set<number>();

  // manage mode
  roleRows: OrgRoleRow[] = [];
  private deletedRoleIds: number[] = [];

  constructor(
    public dialogRef: MatDialogRef<ManageOrgRolesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private backendService: BackendService) {
    this.orgExtId = (dialogData && dialogData.orgExtId) || '';
  }

  ngOnInit(): void {
    if (!this.orgExtId) {
      this.errorMessage = 'Select an organization first.';
      this.loading = false;
      return;
    }
    this.loadCatalog();
  }

  // Case methods return the raw body; some paths wrap it in { data }. Unwrap defensively.
  private unwrapResponse(response: any): any {
    return (response && response.data !== undefined && response.data !== null) ? response.data : response;
  }

  // The org's existing catalog (active + inactive) decides the mode: empty => first-time assign.
  private loadCatalog(): void {
    this.loading = true;
    this.loadFailed = false;
    this.errorMessage = '';
    this.backendService.getOrgCaseRoles(this.orgExtId, false).subscribe((response: any) => {
      const existingOrgRoles: OrgCaseRoleModel[] = this.unwrapResponse(response) || [];
      if (existingOrgRoles.length > 0) {
        this.mode = 'manage';
        this.roleRows = existingOrgRoles.slice()
          .sort((roleA, roleB) => (roleA.displayOrder || 0) - (roleB.displayOrder || 0))
          .map(orgRole => ({
            id: orgRole.id || 0,
            roleCode: orgRole.roleCode || '',
            displayName: orgRole.displayName || '',
            description: orgRole.description || '',
            displayOrder: orgRole.displayOrder || 0,
            isActive: orgRole.isActive !== false
          }));
      } else {
        this.mode = 'assign';
      }
      // Master roles power the assign-mode checkboxes AND the manage-mode Role Name suggestions.
      this.loadMasterRoles();
    }, () => {
      this.errorMessage = 'Unable to load organization roles. Please try again.';
      this.loadFailed = true;
      this.loading = false;
    });
  }

  private loadMasterRoles(): void {
    this.backendService.getCaseMasterRoles().subscribe((response: any) => {
      this.masterRoles = (this.unwrapResponse(response) || []).slice()
        .sort((roleA: any, roleB: any) => (roleA.displayOrder || 0) - (roleB.displayOrder || 0));
      this.loading = false;
    }, () => {
      this.errorMessage = 'Unable to load the master role list. Please try again.';
      this.loadFailed = true;
      this.loading = false;
    });
  }

  // ---------- assign-from-master mode ----------

  isMasterSelected(masterRoleId: number): boolean {
    return this.selectedMasterRoleIds.has(masterRoleId);
  }

  toggleMaster(masterRoleId: number, isChecked: boolean): void {
    if (isChecked) { this.selectedMasterRoleIds.add(masterRoleId); } else { this.selectedMasterRoleIds.delete(masterRoleId); }
  }

  get allMastersSelected(): boolean {
    return this.masterRoles.length > 0 && this.selectedMasterRoleIds.size === this.masterRoles.length;
  }

  toggleSelectAll(shouldSelectAll: boolean): void {
    this.selectedMasterRoleIds = shouldSelectAll ? new Set(this.masterRoles.map(masterRole => masterRole.id)) : new Set<number>();
  }

  get selectedMasterCount(): number {
    return this.selectedMasterRoleIds.size;
  }

  saveAndFinalize(): void {
    if (this.selectedMasterRoleIds.size === 0) {
      this.errorMessage = 'Select at least one role to assign.';
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    const assignRequest = { orgExtId: this.orgExtId, masterRoleIds: Array.from(this.selectedMasterRoleIds) };
    this.backendService.assignOrgRolesFromMaster(assignRequest).subscribe((response: any) => {
      this.saving = false;
      const responseBody = this.unwrapResponse(response);
      if (responseBody && responseBody.success === false) {
        this.errorMessage = responseBody.message || 'Unable to assign roles.';
        return;
      }
      this.dialogRef.close(true);
    }, (error) => {
      this.saving = false;
      this.errorMessage = this.resolveError(error, 'Unable to assign roles.');
    });
  }

  // ---------- manage mode ----------

  addRow(): void {
    const nextDisplayOrder = this.roleRows.length ? Math.max(...this.roleRows.map(role => role.displayOrder || 0)) + 1 : 1;
    this.roleRows.push({ id: 0, roleCode: '', displayName: '', description: '', displayOrder: nextDisplayOrder, isActive: true });
  }

  removeRow(index: number): void {
    const roleRow = this.roleRows[index];
    // A persisted role is deleted explicitly by id; an unsaved row is just dropped from the list.
    if (roleRow.id > 0) { this.deletedRoleIds.push(roleRow.id); }
    this.roleRows.splice(index, 1);
  }

  moveUp(index: number): void {
    if (index <= 0) { return; }
    [this.roleRows[index - 1], this.roleRows[index]] = [this.roleRows[index], this.roleRows[index - 1]];
    this.renumberDisplayOrder();
  }

  moveDown(index: number): void {
    if (index >= this.roleRows.length - 1) { return; }
    [this.roleRows[index + 1], this.roleRows[index]] = [this.roleRows[index], this.roleRows[index + 1]];
    this.renumberDisplayOrder();
  }

  private renumberDisplayOrder(): void {
    this.roleRows.forEach((roleRow, index) => roleRow.displayOrder = index + 1);
  }

  // Master roles filtered by what's typed in a Role Name cell (contains match); drives the
  // mat-autocomplete option list so it matches the app's other dropdowns. Empty value shows all.
  filteredMasterRoles(typedValue: string): CaseMasterRoleModel[] {
    const normalizedValue = (typedValue || '').toString().trim().toLowerCase();
    if (!normalizedValue) { return this.masterRoles || []; }
    return (this.masterRoles || []).filter(masterRole => (masterRole.displayName || '').toLowerCase().includes(normalizedValue));
  }

  // When a Role Name matches a master role (picked from the suggestions or typed), prefill the row's
  // description and role code if they're still empty. Free text remains fully allowed.
  onManageRoleNameChange(roleRow: OrgRoleRow): void {
    const normalizedName = (roleRow.displayName || '').trim().toLowerCase();
    if (!normalizedName) { return; }
    const matchingMasterRole = (this.masterRoles || []).find(masterRole => (masterRole.displayName || '').trim().toLowerCase() === normalizedName);
    if (!matchingMasterRole) { return; }
    if (!(roleRow.description || '').trim()) { roleRow.description = matchingMasterRole.description || ''; }
    if (!(roleRow.roleCode || '').trim()) { roleRow.roleCode = matchingMasterRole.roleCode || ''; }
  }

  saveManage(): void {
    for (const roleRow of this.roleRows) {
      if (!roleRow.displayName || !roleRow.displayName.trim()) {
        this.errorMessage = 'Role name is required for all roles.';
        return;
      }
    }
    const activeRoleNames = this.roleRows.filter(role => role.isActive).map(role => role.displayName.trim().toLowerCase());
    if (activeRoleNames.some((name, index) => activeRoleNames.indexOf(name) !== index)) {
      this.errorMessage = 'Duplicate role names are not allowed.';
      return;
    }
    this.saving = true;
    this.errorMessage = '';
    const saveRequest = {
      orgExtId: this.orgExtId,
      roles: this.roleRows.map((roleRow, index) => ({
        id: roleRow.id || 0,
        roleCode: (roleRow.roleCode || '').trim(),
        displayName: roleRow.displayName.trim(),
        description: roleRow.description || '',
        displayOrder: roleRow.displayOrder != null ? +roleRow.displayOrder : index + 1,
        isActive: roleRow.isActive !== false
      })),
      deletedRoleIds: this.deletedRoleIds
    };
    this.backendService.saveOrgCaseRoles(saveRequest).subscribe((response: any) => {
      this.saving = false;
      const responseBody = this.unwrapResponse(response);
      if (responseBody && responseBody.success === false) {
        this.errorMessage = responseBody.message || 'Unable to save roles.';
        return;
      }
      this.dialogRef.close(true);
    }, (error) => {
      this.saving = false;
      this.errorMessage = this.resolveError(error, 'Unable to save roles.');
    });
  }

  retry(): void {
    this.loadCatalog();
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private resolveError(error: any, fallbackMessage: string): string {
    if (error && error.error && error.error.message) { return error.error.message; }
    if (error && error.message) { return error.message; }
    return fallbackMessage;
  }
}
